import { BUCHAREST_SECTORS, RO_COUNTIES, isValidCnp, isValidCui, isValidIban } from '@financiar/shared';
import type { CreateClientInput, UpdateClientInput } from '@financiar/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { App, Button, Drawer, Form, Input, InputNumber, Radio, Select, Space, Switch, Tooltip } from 'antd';
import { useEffect } from 'react';
import { getApiError } from '../../api/client';
import type { Client } from '../../api/types';
import { applyServerErrors, normalizeCui, normalizeIban, undefinedIfEmpty } from '../../lib/forms';
import { createClient, fetchAnafCompany, fetchClient, updateClient } from './api';

const COUNTY_OPTIONS = RO_COUNTIES.map((county) => ({ value: county.code, label: county.name }));
const SECTOR_OPTIONS = BUCHAREST_SECTORS.map((sector) => ({
  value: sector,
  label: `Sector ${sector.replace('SECTOR', '')}`,
}));
const KIND_OPTIONS = [
  { label: 'Persoană juridică', value: 'COMPANY' },
  { label: 'Persoană fizică', value: 'INDIVIDUAL' },
];

interface ClientFormValues {
  kind: 'COMPANY' | 'INDIVIDUAL';
  name: string;
  cui?: string;
  isVatPayer: boolean;
  regCom?: string;
  cnp?: string;
  addressLine: string;
  countyCode: string;
  cityName: string;
  postalCode?: string;
  email?: string;
  phone?: string;
  contactName?: string;
  bankName?: string;
  iban?: string;
  paymentTermDays?: number;
  isActive: boolean;
}

const CREATE_DEFAULTS: Partial<ClientFormValues> = { kind: 'COMPANY', isVatPayer: false, isActive: true };

/**
 * `/clienti`'s create/edit Drawer. `kind` decides which identity fields show
 * (PJ: CUI + Reg. Com. + Switch TVA; PF: CNP) and is locked once a client
 * exists (`ClientsService.update()` rejects any attempt to change it).
 */
export function ClientFormDrawer({
  open,
  firmaId,
  editingId,
  onClose,
  onCreated,
}: {
  open: boolean;
  firmaId: string;
  editingId: string | null;
  onClose: () => void;
  /** Called with the created row when the drawer is used as an inline
   * "+ Client nou" from another screen (the invoice editor's client Select),
   * which needs the new id in order to select it. Not used by
   * `/clienti`'s own list, which just re-fetches. */
  onCreated?: (client: Client) => void;
}) {
  const { message } = App.useApp();
  const queryClient = useQueryClient();
  const [form] = Form.useForm<ClientFormValues>();
  const isEdit = editingId !== null;
  const kind = Form.useWatch('kind', form);
  const countyCode = Form.useWatch('countyCode', form);

  const { data: client, isLoading: isLoadingClient } = useQuery({
    queryKey: ['clients', 'detail', editingId],
    queryFn: () => fetchClient(editingId as string),
    enabled: open && isEdit,
  });

  useEffect(() => {
    if (!open) return;
    if (isEdit && client) {
      form.setFieldsValue({
        kind: client.kind,
        name: client.name,
        cui: client.cui ?? undefined,
        isVatPayer: client.isVatPayer,
        regCom: client.regCom ?? undefined,
        cnp: client.cnp ?? undefined,
        addressLine: client.addressLine,
        countyCode: client.countyCode,
        cityName: client.cityName,
        postalCode: client.postalCode ?? undefined,
        email: client.email ?? undefined,
        phone: client.phone ?? undefined,
        contactName: client.contactName ?? undefined,
        bankName: client.bankName ?? undefined,
        iban: client.iban ?? undefined,
        paymentTermDays: client.paymentTermDays ?? undefined,
        isActive: client.isActive,
      });
    } else if (!isEdit) {
      form.resetFields();
      form.setFieldsValue(CREATE_DEFAULTS);
    }
  }, [open, isEdit, client, form]);

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['clients', firmaId] });

  const createMutation = useMutation({
    mutationFn: (values: CreateClientInput) => createClient(values),
    onSuccess: (client) => {
      message.success('Clientul a fost creat.');
      invalidate();
      onCreated?.(client);
      onClose();
    },
    onError: (error) => {
      const apiError = getApiError(error);
      if (!applyServerErrors(form, apiError)) message.error(apiError?.message ?? 'Clientul nu a putut fi creat.');
    },
  });

  const updateMutation = useMutation({
    mutationFn: (values: UpdateClientInput) => updateClient(editingId as string, values),
    onSuccess: () => {
      message.success('Clientul a fost actualizat.');
      invalidate();
      onClose();
    },
    onError: (error) => {
      const apiError = getApiError(error);
      if (!applyServerErrors(form, apiError)) message.error(apiError?.message ?? 'Clientul nu a putut fi actualizat.');
    },
  });

  function handleFinish(values: ClientFormValues) {
    const isCompany = values.kind === 'COMPANY';
    if (isEdit) {
      updateMutation.mutate({
        name: values.name.trim(),
        cui: isCompany ? normalizeCui(values.cui) : undefined,
        isVatPayer: isCompany ? values.isVatPayer : false,
        regCom: isCompany ? undefinedIfEmpty(values.regCom) : undefined,
        cnp: isCompany ? undefined : undefinedIfEmpty(values.cnp),
        addressLine: values.addressLine.trim(),
        cityName: values.cityName,
        countyCode: values.countyCode,
        postalCode: undefinedIfEmpty(values.postalCode),
        email: undefinedIfEmpty(values.email),
        phone: undefinedIfEmpty(values.phone),
        contactName: undefinedIfEmpty(values.contactName),
        bankName: undefinedIfEmpty(values.bankName),
        iban: values.iban ? normalizeIban(values.iban) : undefined,
        paymentTermDays: values.paymentTermDays,
        isActive: values.isActive,
      } as UpdateClientInput);
    } else {
      createMutation.mutate({
        companyId: firmaId,
        kind: values.kind,
        name: values.name.trim(),
        cui: isCompany ? normalizeCui(values.cui) : undefined,
        isVatPayer: isCompany ? values.isVatPayer : false,
        regCom: isCompany ? undefinedIfEmpty(values.regCom) : undefined,
        cnp: isCompany ? undefined : undefinedIfEmpty(values.cnp),
        addressLine: values.addressLine.trim(),
        cityName: values.cityName,
        countyCode: values.countyCode,
        postalCode: undefinedIfEmpty(values.postalCode),
        email: undefinedIfEmpty(values.email),
        phone: undefinedIfEmpty(values.phone),
        contactName: undefinedIfEmpty(values.contactName),
        bankName: undefinedIfEmpty(values.bankName),
        iban: values.iban ? normalizeIban(values.iban) : undefined,
        paymentTermDays: values.paymentTermDays,
        isActive: values.isActive,
      } as CreateClientInput);
    }
  }

  const isSaving = createMutation.isPending || updateMutation.isPending;

  // "Preia date ANAF": interoghează registrul public după CUI și precompletează
  // câmpurile firmei. Suprascrie doar câmpurile pe care ANAF le returnează
  // (cele null/necunoscute rămân neatinse). Județul se setează înaintea
  // localității pentru că `cityName` depinde de el (Select sector la RO-B).
  const anafMutation = useMutation({
    mutationFn: (cui: string) => fetchAnafCompany(cui),
    onSuccess: (data) => {
      if (data.countyCode) form.setFieldValue('countyCode', data.countyCode);
      const patch: Partial<ClientFormValues> = {
        name: data.name || form.getFieldValue('name'),
        isVatPayer: data.isVatPayer,
      };
      if (data.regCom) patch.regCom = data.regCom;
      if (data.addressLine) patch.addressLine = data.addressLine;
      if (data.cityName) patch.cityName = data.cityName;
      if (data.postalCode) patch.postalCode = data.postalCode;
      form.setFieldsValue(patch);
      message.success('Datele au fost preluate din registrul ANAF.');
    },
    onError: (error) => {
      const apiError = getApiError(error);
      message.error(apiError?.message ?? 'Datele nu au putut fi preluate de la ANAF.');
    },
  });

  function handleAnafLookup() {
    const cui = normalizeCui(form.getFieldValue('cui') ?? '');
    if (!isValidCui(cui)) {
      message.warning('Introdu un CUI valid înainte de a prelua datele.');
      return;
    }
    anafMutation.mutate(cui);
  }

  const canLookupAnaf = kind === 'COMPANY';

  return (
    <Drawer
      title={isEdit ? 'Editează clientul' : 'Adaugă client'}
      width="clamp(560px, 40vw, 960px)"
      open={open}
      onClose={onClose}
      destroyOnHidden
      extra={
        <Space>
          <Tooltip title={canLookupAnaf ? 'Completează câmpurile din registrul public ANAF, după CUI' : 'Disponibil doar pentru persoane juridice'}>
            <Button disabled={!canLookupAnaf} loading={anafMutation.isPending} onClick={handleAnafLookup}>
              Preia date ANAF
            </Button>
          </Tooltip>
          <Button type="primary" onClick={() => form.submit()} loading={isSaving}>
            Salvează
          </Button>
        </Space>
      }
    >
      <Form<ClientFormValues> form={form} layout="vertical" onFinish={handleFinish} disabled={isEdit && isLoadingClient}>
        <Form.Item name="kind" label="Tip client" rules={[{ required: true }]}>
          <Radio.Group disabled={isEdit} options={KIND_OPTIONS} optionType="button" />
        </Form.Item>
        <Form.Item
          name="name"
          label={kind === 'INDIVIDUAL' ? 'Nume' : 'Denumire'}
          rules={[{ required: true, message: 'Numele este obligatoriu.' }]}
        >
          <Input />
        </Form.Item>

        {kind === 'INDIVIDUAL' ? (
          <Form.Item
            name="cnp"
            label="CNP"
            rules={[
              {
                validator: (_, value: string | undefined) =>
                  !value || isValidCnp(value.trim()) ? Promise.resolve() : Promise.reject(new Error('CNP invalid.')),
              },
            ]}
          >
            <Input />
          </Form.Item>
        ) : (
          <>
            <Form.Item
              name="cui"
              label="CUI"
              rules={[
                { required: true, message: 'CUI este obligatoriu.' },
                {
                  validator: (_, value: string) =>
                    isValidCui(normalizeCui(value)) ? Promise.resolve() : Promise.reject(new Error('CUI invalid.')),
                },
              ]}
            >
              <Input placeholder="RO12345678 sau 12345678" />
            </Form.Item>
            <Form.Item name="regCom" label="Reg. Com.">
              <Input placeholder="J40/1234/2024" />
            </Form.Item>
            <Form.Item name="isVatPayer" label="Plătitor de TVA" valuePropName="checked">
              <Switch />
            </Form.Item>
          </>
        )}

        <Form.Item name="addressLine" label="Adresă" rules={[{ required: true, message: 'Adresa este obligatorie.' }]}>
          <Input />
        </Form.Item>
        <Form.Item name="countyCode" label="Județ" rules={[{ required: true, message: 'Județul este obligatoriu.' }]}>
          <Select
            options={COUNTY_OPTIONS}
            showSearch
            optionFilterProp="label"
            onChange={() => form.setFieldValue('cityName', undefined)}
          />
        </Form.Item>
        <Form.Item name="cityName" label="Localitate" rules={[{ required: true, message: 'Localitatea este obligatorie.' }]}>
          {countyCode === 'RO-B' ? <Select options={SECTOR_OPTIONS} /> : <Input />}
        </Form.Item>
        <Form.Item name="postalCode" label="Cod poștal">
          <Input />
        </Form.Item>
        <Form.Item name="email" label="Email" rules={[{ type: 'email', message: 'Email invalid.' }]}>
          <Input />
        </Form.Item>
        <Form.Item name="phone" label="Telefon">
          <Input />
        </Form.Item>
        <Form.Item name="contactName" label="Persoană de contact">
          <Input />
        </Form.Item>
        <Form.Item name="bankName" label="Bancă">
          <Input />
        </Form.Item>
        <Form.Item
          name="iban"
          label="IBAN"
          rules={[
            {
              validator: (_, value: string | undefined) =>
                !value || isValidIban(normalizeIban(value)) ? Promise.resolve() : Promise.reject(new Error('IBAN invalid.')),
            },
          ]}
        >
          <Input />
        </Form.Item>
        <Form.Item name="paymentTermDays" label="Termen de plată (zile)">
          <InputNumber min={0} precision={0} style={{ width: '100%' }} />
        </Form.Item>
        <Form.Item name="isActive" label="Activ" valuePropName="checked">
          <Switch />
        </Form.Item>
      </Form>
    </Drawer>
  );
}
